/**
 * Conmutación de emergencia del host de la API (sin datos personales).
 *
 * El frontend lleva empaquetado un único host de API (`REACT_APP_BACKEND_URL`; en producción
 * `https://api.tuplazaia.com`, que pasa por Cloudflare). Cuando ese host no responde (error de
 * red, tiempo agotado o un 52x de Cloudflare) pero la misma API sí responde por su dominio de
 * Railway, todas las peticiones de la sesión (axios global, `apiClient` y `fetch`) se reescriben
 * al host de reserva. En condiciones normales este módulo no interviene: solo cambia de host si
 * el principal falla y el de reserva responde 200 en `/api/health`, y vuelve al principal en
 * cuanto este responde otra vez.
 *
 * Host de reserva: `REACT_APP_BACKEND_FALLBACK_URL` o, si el host principal es de tuplazaia.com,
 * el dominio de Railway documentado en LANZAMIENTO.md. En desarrollo local o en CI (host
 * principal `localhost`) queda desactivado. La decisión se recuerda en sessionStorage durante
 * 10 minutos para que una recarga no vuelva a esperar al host caído; en memoria dura toda la
 * sesión. La sesión de la alumna no se pierde: la autenticación va por cabecera Bearer, no por
 * cookies del dominio.
 */
import axios from 'axios';
import { API_BASE_URL } from './api';

const DEFAULT_FALLBACK = 'https://backend-production-fefbb.up.railway.app';
const STORAGE_KEY = 'tuplazaia.api_failover';
const STORAGE_TTL_MS = 10 * 60 * 1000;
const PROBE_TIMEOUT_MS = 5000;
const PROBE_COOLDOWN_MS = 30 * 1000;
// Sin respuesta (red, tiempo agotado) o códigos de pasarela/Cloudflare: el host no llega a la API.
const HOST_FAILURE_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 530]);

const stripSlash = (url) => (typeof url === 'string' ? url.replace(/\/+$/, '') : '');

const hostnameOf = (url) => {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
};

const isAbsoluteUrl = (url) => typeof url === 'string' && /^https?:\/\//i.test(url);
const isTuPlazaHost = (url) => /(^|\.)tuplazaia\.com$/i.test(hostnameOf(url));

export const PRIMARY_API_URL = stripSlash(API_BASE_URL);

export const FALLBACK_API_URL = (() => {
  const configured = stripSlash(process.env.REACT_APP_BACKEND_FALLBACK_URL || '');
  const candidate = configured || (isTuPlazaHost(PRIMARY_API_URL) ? DEFAULT_FALLBACK : '');
  if (!candidate || !PRIMARY_API_URL || candidate === PRIMARY_API_URL) return '';
  return candidate;
})();

const nativeFetch =
  typeof window !== 'undefined' && typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

let activeHost = '';
let probePromise = null;
let lastProbeAt = 0;

const readStorage = () => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return '';
    const data = JSON.parse(raw);
    if (data && data.host === FALLBACK_API_URL && Number(data.until) > Date.now()) return data.host;
  } catch {
    // Sin sessionStorage (modo privado, entorno de pruebas): se decide solo en memoria.
  }
  return '';
};

const writeStorage = (host) => {
  try {
    if (host) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ host, until: Date.now() + STORAGE_TTL_MS }));
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Idem: la caché es opcional.
  }
};

export const getActiveApiUrl = () => activeHost || PRIMARY_API_URL;
export const isFailoverActive = () => Boolean(activeHost);

const notify = () => {
  try {
    window.dispatchEvent(
      new CustomEvent('tuplazaia:api-failover', { detail: { active: Boolean(activeHost), host: getActiveApiUrl() } })
    );
  } catch {
    // Entornos sin CustomEvent: el aviso es informativo.
  }
};

export const activateFailover = () => {
  if (!FALLBACK_API_URL || activeHost === FALLBACK_API_URL) return;
  activeHost = FALLBACK_API_URL;
  writeStorage(activeHost);
  console.warn(`[api] ${hostnameOf(PRIMARY_API_URL)} no responde; usando ${hostnameOf(activeHost)}`);
  notify();
};

export const deactivateFailover = () => {
  if (!activeHost) return;
  activeHost = '';
  writeStorage('');
  notify();
};

/** Reescribe una URL absoluta del host principal al host activo (solo con la conmutación activa). */
export const rewriteUrl = (url) => {
  if (!activeHost || typeof url !== 'string' || !PRIMARY_API_URL) return url;
  if (url === PRIMARY_API_URL) return activeHost;
  if (url.startsWith(`${PRIMARY_API_URL}/`)) return activeHost + url.slice(PRIMARY_API_URL.length);
  return url;
};

const probeHealth = async (base) => {
  if (!nativeFetch) return false;
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS) : null;
  try {
    const res = await nativeFetch(`${base}/api/health`, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller ? controller.signal : undefined,
    });
    return Boolean(res && res.ok);
  } catch {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
};

/**
 * Comprueba el host principal y, si no responde pero el de reserva sí, activa la conmutación
 * (y la retira si el principal vuelve a responder). Devuelve true si tras la comprobación la
 * conmutación está activa. Nunca lanza; una comprobación en curso se comparte y entre dos
 * comprobaciones hay 30 s de espera salvo `force`.
 */
export const checkAndFailover = ({ force = false } = {}) => {
  if (!FALLBACK_API_URL) return Promise.resolve(false);
  if (probePromise) return probePromise;
  if (!force && Date.now() - lastProbeAt < PROBE_COOLDOWN_MS) return Promise.resolve(Boolean(activeHost));
  lastProbeAt = Date.now();
  probePromise = (async () => {
    try {
      if (await probeHealth(PRIMARY_API_URL)) {
        deactivateFailover();
        return false;
      }
      if (await probeHealth(FALLBACK_API_URL)) {
        activateFailover();
        return true;
      }
      return Boolean(activeHost);
    } finally {
      probePromise = null;
    }
  })();
  return probePromise;
};

const requestTargetsPrimary = (config) => {
  if (!config || !PRIMARY_API_URL) return false;
  if (isAbsoluteUrl(config.url)) {
    return config.url === PRIMARY_API_URL || config.url.startsWith(`${PRIMARY_API_URL}/`);
  }
  return stripSlash(config.baseURL || '') === PRIMARY_API_URL;
};

const looksLikeHostFailure = (error) => {
  if (!error || axios.isCancel(error)) return false;
  if (!error.response) return true;
  return HOST_FAILURE_STATUSES.has(error.response.status);
};

/**
 * Instala en una instancia de axios la reescritura de host (petición) y el reintento único
 * contra el host de reserva cuando el principal falla (respuesta). Idempotente.
 */
export const installAxiosFailover = (instance) => {
  if (!instance || !FALLBACK_API_URL || instance.__tuplazaiaFailover) return instance;
  instance.__tuplazaiaFailover = true;
  instance.interceptors.request.use((config) => {
    if (!activeHost) return config;
    if (isAbsoluteUrl(config.url)) {
      config.url = rewriteUrl(config.url);
    } else if (typeof config.baseURL === 'string' && config.baseURL) {
      config.baseURL = rewriteUrl(stripSlash(config.baseURL));
    }
    return config;
  });
  instance.interceptors.response.use(undefined, async (error) => {
    const config = error && error.config;
    if (!config || config.__tuplazaiaFailoverRetried || !looksLikeHostFailure(error) || !requestTargetsPrimary(config)) {
      throw error;
    }
    const switched = activeHost ? true : await checkAndFailover();
    if (!switched) throw error;
    config.__tuplazaiaFailoverRetried = true;
    return instance.request(config);
  });
  return instance;
};

/** Comprobación al arrancar la app: detecta el host caído antes de la primera petición real. */
export const startApiFailoverWatch = () => {
  if (!FALLBACK_API_URL || !nativeFetch || activeHost) return Promise.resolve(Boolean(activeHost));
  return checkAndFailover({ force: true }).catch(() => false);
};

activeHost = readStorage();
installAxiosFailover(axios);

if (nativeFetch && FALLBACK_API_URL) {
  // Las páginas que usan fetch directamente con `${BACKEND_URL}/api/...` también cambian de host.
  window.fetch = function tuplazaiaFetch(input, init) {
    if (activeHost) {
      if (typeof input === 'string') return nativeFetch(rewriteUrl(input), init);
      if (typeof URL === 'function' && input instanceof URL) return nativeFetch(rewriteUrl(input.toString()), init);
      if (input && typeof input.url === 'string' && rewriteUrl(input.url) !== input.url) {
        return nativeFetch(new Request(rewriteUrl(input.url), input), init);
      }
    }
    return nativeFetch(input, init);
  };
}
